import { CreateBackupQueueResponse } from '@/@apis/desktop-api'
import Button from '@/components/ui/Button'
import Dialog from '@/components/ui/Dialog'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useTranslation } from 'react-i18next'
import { CgCloseO } from 'react-icons/cg'
import { HiOutlineExclamation } from 'react-icons/hi'
import Avatar from '../Avatar'

interface ConfirmDialogProps {
   createBackupQueueResponse: CreateBackupQueueResponse
}

const BusinessLogicErrors = ({ createBackupQueueResponse }: ConfirmDialogProps) => {
   if (!createBackupQueueResponse.errorDescription) {
      return null
   }

   return (
      <div className="max-h-[70vh] overflow-auto">
         <div className="mb-4">
            <div className={'bg-red-100 px-4 py-3 rounded-md font-semibold text-red-600 dark:text-red-900 mb-4'}>Hata Detayları</div>

            <ul className="text-gray-500 list-inside dark:text-gray-400">
               <li className="text-body-color dark:text-dark-6 flex text-sm mb-1.5 px-0.5">
                  <span className="mr-2.5 flex items-center text-sm text-red-600">
                     <svg width={20} height={20} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_980_24913)">
                           <path
                              d="M10 0.5625C4.78125 0.5625 0.5625 4.78125 0.5625 10C0.5625 15.2188 4.78125 19.4688 10 19.4688C15.2188 19.4688 19.4688 15.2188 19.4688 10C19.4688 4.78125 15.2188 0.5625 10 0.5625ZM10 18.0625C5.5625 18.0625 1.96875 14.4375 1.96875 10C1.96875 5.5625 5.5625 1.96875 10 1.96875C14.4375 1.96875 18.0625 5.59375 18.0625 10.0312C18.0625 14.4375 14.4375 18.0625 10 18.0625Z"
                              fill="currentColor"
                           />
                           <path
                              d="M12.875 7.09375C12.5938 6.8125 12.1563 6.8125 11.875 7.09375L10 9L8.09375 7.09375C7.8125 6.8125 7.375 6.8125 7.09375 7.09375C6.8125 7.375 6.8125 7.8125 7.09375 8.09375L9 10L7.09375 11.9062C6.8125 12.1875 6.8125 12.625 7.09375 12.9062C7.21875 13.0312 7.40625 13.125 7.59375 13.125C7.78125 13.125 7.96875 13.0625 8.09375 12.9062L10 11L11.9062 12.9062C12.0312 13.0312 12.2188 13.125 12.4063 13.125C12.5938 13.125 12.7812 13.0625 12.9062 12.9062C13.1875 12.625 13.1875 12.1875 12.9062 11.9062L11 10L12.9062 8.09375C13.1562 7.8125 13.1563 7.375 12.875 7.09375Z"
                              fill="currentColor"
                           />
                        </g>
                        <defs>
                           <clipPath id="clip0_980_24913">
                              <rect width={20} height={20} fill="white" />
                           </clipPath>
                        </defs>
                     </svg>
                  </span>
                  <span style={{ wordBreak: 'break-all' }}>{createBackupQueueResponse.errorDescription}</span>
               </li>
            </ul>
         </div>
      </div>
   )
}

type SchematronErrorVariable = {
   field: string
   value: string
}

type SchematronError = {
   message: string
   variables: SchematronErrorVariable[]
}

const SchemaError = ({ createBackupQueueResponse }: ConfirmDialogProps) => {
   if (
      createBackupQueueResponse.schemaErrors == null ||
      (createBackupQueueResponse.schemaErrors != null && createBackupQueueResponse.schemaErrors?.length === 0)
   ) {
      return null
   }

   return (
      <>
         <div className={'bg-red-100 px-4 py-3 rounded-md font-semibold text-red-600 dark:text-red-900 mb-4'}>Şema Kontrol Sonucu Hatalı</div>
         <div className="mb-4">
            <ul className="text-gray-500 list-inside dark:text-gray-400">
               {createBackupQueueResponse.schemaErrors!.map((error) => {
                  return (
                     <li className="text-body-color dark:text-dark-6 flex text-sm mb-4 px-0.5">
                        <span className="mr-2.5 flex  text-sm text-red-600">
                           <svg width={20} height={20} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <g clipPath="url(#clip0_980_24913)">
                                 <path
                                    d="M10 0.5625C4.78125 0.5625 0.5625 4.78125 0.5625 10C0.5625 15.2188 4.78125 19.4688 10 19.4688C15.2188 19.4688 19.4688 15.2188 19.4688 10C19.4688 4.78125 15.2188 0.5625 10 0.5625ZM10 18.0625C5.5625 18.0625 1.96875 14.4375 1.96875 10C1.96875 5.5625 5.5625 1.96875 10 1.96875C14.4375 1.96875 18.0625 5.59375 18.0625 10.0312C18.0625 14.4375 14.4375 18.0625 10 18.0625Z"
                                    fill="currentColor"
                                 />
                                 <path
                                    d="M12.875 7.09375C12.5938 6.8125 12.1563 6.8125 11.875 7.09375L10 9L8.09375 7.09375C7.8125 6.8125 7.375 6.8125 7.09375 7.09375C6.8125 7.375 6.8125 7.8125 7.09375 8.09375L9 10L7.09375 11.9062C6.8125 12.1875 6.8125 12.625 7.09375 12.9062C7.21875 13.0312 7.40625 13.125 7.59375 13.125C7.78125 13.125 7.96875 13.0625 8.09375 12.9062L10 11L11.9062 12.9062C12.0312 13.0312 12.2188 13.125 12.4063 13.125C12.5938 13.125 12.7812 13.0625 12.9062 12.9062C13.1875 12.625 13.1875 12.1875 12.9062 11.9062L11 10L12.9062 8.09375C13.1562 7.8125 13.1563 7.375 12.875 7.09375Z"
                                    fill="currentColor"
                                 />
                              </g>
                              <defs>
                                 <clipPath id="clip0_980_24913">
                                    <rect width={20} height={20} fill="white" />
                                 </clipPath>
                              </defs>
                           </svg>
                        </span>
                        <div
                           className="scheme-inner-text"
                           style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                           dangerouslySetInnerHTML={{ __html: error }}
                        ></div>
                        {/* <pre className="whitespace-pre-wrap">{error}</pre> */}
                     </li>
                  )
               })}
            </ul>
         </div>
      </>
   )
}

const SchmatronError = ({ createBackupQueueResponse }: ConfirmDialogProps) => {
   if (createBackupQueueResponse.schematronErrors === null) {
      return null
   }

   const { t } = useTranslation(undefined, { keyPrefix: 'views.eledgerQueues.errorDetail.schmeatronFields' })

   return (
      <>
         <div className={'bg-red-100 px-4 py-3 rounded-md font-semibold text-red-600 dark:text-red-900 mb-4'}>Şematron Kontrol Sonucu Hatalı</div>
         <div className="mb-4">
            <ul className="text-gray-500 list-inside dark:text-gray-400 pr-3">
               {createBackupQueueResponse.schematronErrors!.map((error) => {
                  const errorObj: SchematronError = JSON.parse(error)

                  return (
                     <li className="text-body-color dark:text-dark-6 flex text-sm mb-4 px-0.5 flex-row">
                        <div className="flex align-center">
                           <span className="mr-2.5 flex  text-sm text-red-600">
                              <svg width={20} height={20} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                 <g clipPath="url(#clip0_980_24913)">
                                    <path
                                       d="M10 0.5625C4.78125 0.5625 0.5625 4.78125 0.5625 10C0.5625 15.2188 4.78125 19.4688 10 19.4688C15.2188 19.4688 19.4688 15.2188 19.4688 10C19.4688 4.78125 15.2188 0.5625 10 0.5625ZM10 18.0625C5.5625 18.0625 1.96875 14.4375 1.96875 10C1.96875 5.5625 5.5625 1.96875 10 1.96875C14.4375 1.96875 18.0625 5.59375 18.0625 10.0312C18.0625 14.4375 14.4375 18.0625 10 18.0625Z"
                                       fill="currentColor"
                                    />
                                    <path
                                       d="M12.875 7.09375C12.5938 6.8125 12.1563 6.8125 11.875 7.09375L10 9L8.09375 7.09375C7.8125 6.8125 7.375 6.8125 7.09375 7.09375C6.8125 7.375 6.8125 7.8125 7.09375 8.09375L9 10L7.09375 11.9062C6.8125 12.1875 6.8125 12.625 7.09375 12.9062C7.21875 13.0312 7.40625 13.125 7.59375 13.125C7.78125 13.125 7.96875 13.0625 8.09375 12.9062L10 11L11.9062 12.9062C12.0312 13.0312 12.2188 13.125 12.4063 13.125C12.5938 13.125 12.7812 13.0625 12.9062 12.9062C13.1875 12.625 13.1875 12.1875 12.9062 11.9062L11 10L12.9062 8.09375C13.1562 7.8125 13.1563 7.375 12.875 7.09375Z"
                                       fill="currentColor"
                                    />
                                 </g>
                                 <defs>
                                    <clipPath id="clip0_980_24913">
                                       <rect width={20} height={20} fill="white" />
                                    </clipPath>
                                 </defs>
                              </svg>
                           </span>
                        </div>

                        <div className="w-full">
                           <span className="schema-detail-text">{errorObj.message}</span>

                           {errorObj.variables.length === 1 && (
                              <span>
                                 {' '}
                                 <b>Hatalı Yevmiye No : </b>
                                 {errorObj.variables[0].value}
                              </span>
                           )}
                           {errorObj.variables.length > 1 && (
                              <table className="schema-detail">
                                 <thead>
                                    <tr>
                                       {errorObj.variables.map((variable) => {
                                          return <th>{t(variable.field)}</th>
                                       })}
                                    </tr>
                                 </thead>
                                 <tbody>
                                    <tr>
                                       {errorObj.variables.map((variable) => {
                                          return <td>{variable.value}</td>
                                       })}
                                    </tr>
                                 </tbody>
                              </table>
                           )}
                        </div>

                        {/* <pre className="whitespace-pre-wrap">{error}</pre> */}
                     </li>
                  )
               })}
            </ul>
         </div>
      </>
   )
}

const SchemaSchematronErrors = ({ createBackupQueueResponse }: ConfirmDialogProps) => {
   return (
      <div className="max-h-[60vh] overflow-auto">
         <SchmatronError createBackupQueueResponse={createBackupQueueResponse} />

         <SchemaError createBackupQueueResponse={createBackupQueueResponse} />
      </div>
   )
}

const BackupResponseErrorDetailModal = NiceModal.create(({ createBackupQueueResponse }: ConfirmDialogProps) => {
   const modal = useModal()

   const { t } = useTranslation(undefined, { keyPrefix: 'components.shared.errorDialog' })

   const handleCancel = () => {
      modal.hide()
   }

   return (
      <Dialog
         shouldCloseOnEsc={true}
         shouldCloseOnOverlayClick={true}
         width={900}
         onClose={handleCancel}
         onRequestClose={handleCancel}
         isOpen={modal.visible}
         style={{ content: { zIndex: 999999 }, overlay: { zIndex: 999999999 } }}
      >
         <div className=" pb-6 pt-2 flex items-center">
            <div>
               <Avatar className="text-red-600 bg-red-100 dark:text-red-100" shape="circle">
                  <span className="text-2xl">
                     <HiOutlineExclamation />
                  </span>
               </Avatar>
            </div>
            <div className="ml-4 rtl:mr-4">
               <h5 className="mb-2">{t('title')}</h5>
            </div>
         </div>

         <BusinessLogicErrors createBackupQueueResponse={createBackupQueueResponse} />

         <SchemaSchematronErrors createBackupQueueResponse={createBackupQueueResponse} />

         <div className="text-right mt-6">
            <Button className="ltr:mr-2 rtl:ml-2" variant="twoTone" color="red-600" onClick={handleCancel} icon={<CgCloseO />}>
               {t('cancelButton')}
            </Button>
         </div>
      </Dialog>
   )
})

export default BackupResponseErrorDetailModal
